import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import SelfRegisteredBadge from "@/components/SelfRegisteredBadge";
import { Dog, ClipboardList, MessageSquareQuote, History, StickyNote, ExternalLink, Loader2, Phone, Mail, PhoneCall, MessageSquare, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (d) => { try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return d || ""; } };
const age = (b) => { const t = Date.parse(b); if (!t) return ""; const y = (Date.now() - t) / 3.156e10; return y < 1 ? `${Math.max(1, Math.round(y * 12))} mo` : `${Math.floor(y)} yrs`; };
const ICON = { call: PhoneCall, called: PhoneCall, left_voicemail: PhoneCall, email: Mail, email_sent: Mail, sms: MessageSquare, text: MessageSquare };
const LABEL = { call: "Called", called: "Called", left_voicemail: "Called — left voicemail", email: "Emailed", email_sent: "Emailed", sms: "Texted",
  text: "Texted", comment: "Note", skip: "Skipped", reply_received: "They replied", completed: "Completed", follow_up_complete: "Follow-up complete" };

const Box = ({ icon: Icon, title, children, tid }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={tid}>
    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2"><Icon className="w-3.5 h-3.5" />{title}</div>
    {children}
  </div>
);

const Dogs = ({ f }) => {
  const p = f.profile || {};
  if (f.gingr_dogs?.length) return (
    <div className="space-y-2 text-sm">
      {f.gingr_dogs.map((a, i) => {
        const exp = a.vaccines_expire && Date.parse(a.vaccines_expire) < Date.now();
        return (
          <div key={i} data-testid={`focus-dog-${i}`}>
            <b>{a.name || "Unnamed"}</b>
            <span className="text-slate-500">{[a.gender, age(a.birthday), a.weight && `${a.weight} lb`, a.fixed && "fixed"].filter(Boolean).map((x) => ` · ${x}`).join("")}</span>
            {a.vaccines_expire && <span className={`ml-1 text-xs ${exp ? "text-rose-600" : "text-emerald-600"}`}>{exp ? "⚠ vaccines expired" : `✓ vaccines to ${fmt(a.vaccines_expire)}`}</span>}
            {(a.medicines || a.allergies) && <div className="text-xs text-amber-700">{[a.medicines && `Meds: ${a.medicines}`, a.allergies && `Allergies: ${a.allergies}`].filter(Boolean).join(" · ")}</div>}
          </div>
        );
      })}
    </div>
  );
  const bits = [p.breed, p.age, p.size].filter(Boolean).join(" · ");
  return (
    <div className="text-sm space-y-1">
      <div><b>{p.dog_names?.length ? p.dog_names.join(", ") : p.dog_count ? `${p.dog_count} dog${p.dog_count > 1 ? "s" : ""}` : "Not mentioned"}</b>{bits && <span className="text-slate-500"> · {bits}</span>}</div>
      {p.concerns?.length > 0 && <div className="text-xs text-amber-700">Notes: {p.concerns.join(" · ")}</div>}
    </div>
  );
};

// Right pane of focus mode: everything a rep needs to confirm the draft is right.
export const FocusDetails = ({ leadId, gingrUrl }) => {
  const [f, setF] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/api/smart-leads/focus/${leadId}`).then((r) => setF(r.data)).catch(() => setF({ error: true }));
  useEffect(() => { setF(null); setNote(""); load(); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps
  const addNote = async () => {
    setBusy(true);
    try { await axios.post(`${API}/api/leads/${leadId}/actions`, { type: "comment", notes: note.trim() }); setNote(""); toast.success("Note saved to lead"); load(); }
    catch { toast.error("Could not save note"); } finally { setBusy(false); }
  };
  if (!f) return <div className="flex items-center gap-2 text-slate-400 p-8" data-testid="focus-details-loading"><Loader2 className="w-4 h-4 animate-spin" />Loading lead details…</div>;
  if (f.error) return <div className="p-8 text-rose-600 text-sm">Couldn't load lead details.</div>;
  const p = f.profile || {};
  const initials = (f.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="space-y-4" data-testid="focus-details">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-700 font-extrabold flex items-center justify-center text-lg shrink-0">{initials}</div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2"><span className="text-xl font-extrabold text-slate-900" data-testid="focus-details-name">{f.name}</span>{f.self_registered && <SelfRegisteredBadge size="sm" />}</div>
          <div className="text-sm text-slate-500 flex flex-wrap gap-x-3">
            {f.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{f.email}</span>}
            {f.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{f.phone}</span>}
            {f.address && <span>{f.address}</span>}
          </div>
          <div className="flex flex-wrap gap-3 mt-2 text-xs">
            {f.gingr_owner_id ? <span className="font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">In Gingr #{f.gingr_owner_id}</span>
              : <span className="font-bold text-slate-500 bg-slate-100 rounded px-2 py-0.5">Not in Gingr</span>}
            {f.gingr_owner_id && gingrUrl && <a className="font-bold text-indigo-600 hover:underline inline-flex items-center gap-1" href={`${gingrUrl}/owners/view/id/${f.gingr_owner_id}`} target="_blank" rel="noreferrer" data-testid="focus-open-gingr">Open in Gingr <ExternalLink className="w-3 h-3" /></a>}
            <a className="font-bold text-indigo-600 hover:underline inline-flex items-center gap-1" href={`/leads/${leadId}?from=queue`} target="_blank" rel="noreferrer" data-testid="focus-open-lead">Full lead page <ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
        {f.score != null && <div className="ml-auto text-right shrink-0"><div className="text-xs text-slate-400">AI priority</div><div className="text-2xl font-extrabold text-rose-600">{f.score}</div></div>}
      </div>
      {f.why && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">{f.why}</div>}
      <div className="grid md:grid-cols-2 gap-4">
        <Box icon={Dog} title="Dogs" tid="focus-dogs"><Dogs f={f} /></Box>
        <Box icon={ClipboardList} title="Request" tid="focus-request">
          <div className="text-sm space-y-1">
            <div>Service: <b>{p.service || "—"}</b></div>
            {p.dates?.length > 0 && <div>Dates: <b>{p.dates.map(fmt).join(" – ")}</b></div>}
            {p.trip_reason && <div>Reason: <b>{p.trip_reason}</b></div>}
            <div>Best time to reach: <b>{p.best_time || "Anytime"}</b></div>
            <div>SMS: <b className={f.sms_ok ? "text-emerald-700" : "text-rose-600"}>{f.sms_why}</b></div>
            <div>Email: <b className={f.email_ok ? "text-emerald-700" : "text-rose-600"}>{f.email_ok ? "Allowed" : f.email ? "Opted out" : "No email"}</b></div>
            <div className="text-xs text-slate-400">Lead since {fmt(f.created_at)} · {f.status}</div>
          </div>
        </Box>
      </div>
      <Box icon={MessageSquareQuote} title="Their message (web form)" tid="focus-message">
        <div className="text-sm text-slate-700 bg-slate-50 rounded-xl p-3 italic whitespace-pre-wrap">{p.details ? `"${p.details}"` : "No message on the form."}</div>
        {p.notes && <div className="text-xs text-slate-500 mt-2">Lead notes: {p.notes}</div>}
      </Box>
      <Box icon={History} title="Outreach history" tid="focus-history">
        {f.history?.length ? (
          <div className="space-y-2 text-sm">
            {f.history.map((h, i) => { const I = ICON[h.type] || FileText; return (
              <div key={i} className="flex gap-3"><span className="text-slate-400 w-24 shrink-0">{fmt(h.date)}</span>
                <I className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <span className="min-w-0"><b className="font-semibold">{LABEL[h.type] || h.type}</b>{h.notes && <span className="text-slate-600"> — {h.notes}</span>}{h.by && <span className="text-slate-400"> · {h.by}</span>}</span>
              </div>); })}
          </div>
        ) : <div className="text-sm text-slate-400">No outreach logged yet.</div>}
      </Box>
      <Box icon={StickyNote} title="Notes" tid="focus-notes">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note for the team (saved to the lead)…" data-testid="focus-note-input" />
        <div className="flex justify-end mt-2"><Button size="sm" variant="outline" disabled={!note.trim() || busy} onClick={addNote} data-testid="focus-note-save">Save note</Button></div>
      </Box>
    </div>
  );
};
